import type { IParseOptions } from "qs";
import qs from "qs";
import { type SetStateAction, useState } from "react";
import { useMemo, useRef, useEffect } from "react";
import { useLocation } from "react-router";
import { useMemoizedFn } from "@exonn/hooks";
import type { Router } from "@remix-run/router";
import type { ZodType } from "zod";
import { logger } from "src/logger";

export interface Options {
  router?: Router;
  navigateMode?: "push" | "replace";
  deserialize?: (str: string) => Record<string, any>;
  serialize?: (obj: Record<string, any>) => string;
  disablePersistence?: boolean;
  validationSchema?: ZodType;
}

type UrlState = Record<string, any>;

const qsDeserializer = (str: string): Record<string, any> => {
  const decoder: Required<IParseOptions>["decoder"] = (
    str: string,
    defaultDecoder: qs.defaultDecoder,
    charset: string,
    type: "key" | "value",
  ) => {
    return coerceValuesDecoder(
      str,
      // preservePlusDecoder(str, type),
      defaultDecoder,
      charset,
      type,
    );
  };
  return qs.parse(str, {
    ignoreQueryPrefix: true,
    decoder: decoder,
  });
};

const qsSerializer = (obj: Record<string, any>): string => {
  return qs.stringify(obj, {
    // encode: false,
    encodeValuesOnly: true,
  });
};

const useUrlState = <S extends UrlState = UrlState>(
  router: Router,
  initialState?: S | (() => S),
  options?: Options,
) => {
  type State = Partial<{ [key in keyof S]: any }>;

  const {
    navigateMode = "push",
    serialize = qsSerializer,
    deserialize = qsDeserializer,
    disablePersistence = false,
    validationSchema,
  } = options || {};

  const location = useLocation();

  const initialStateRef = useRef(
    typeof initialState === "function"
      ? (initialState as () => S)()
      : initialState || {},
  );

  const [localState, setLocalState] = useState<Partial<S>>(
    initialStateRef.current,
  );

  const queryFromUrl = useMemo(() => {
    const unsafe = deserialize(location.search);
    if (validationSchema) {
      const result = validationSchema.safeParse(unsafe);
      if (result.success) {
        return unsafe;
      }
      logger.error(result.error, {
        value: unsafe,
      });
      return {
        ...initialStateRef.current,
      };
    }
    return unsafe;
  }, [location.search, deserialize, validationSchema]);

  const targetQuery: Partial<S> = useMemo(
    () => ({
      ...initialStateRef.current,
      ...queryFromUrl,
    }),
    [queryFromUrl],
  );

  // workaround infinite loop
  // https://github.com/mui/mui-x/issues/13230
  const startNavigationTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      clearTimeout(startNavigationTimeout.current);
    };
  }, []);

  const setState = (
    s: SetStateAction<State>,
    localOptions?: {
      navigateMode?: "push" | "replace";
      onFinish?: () => void;
    },
  ) => {
    const newQuery = typeof s === "function" ? s(targetQuery) : s;
    if (disablePersistence) {
      setLocalState((prev) => ({ ...prev, ...newQuery }));
      localOptions?.onFinish?.();
      return;
    }

    clearTimeout(startNavigationTimeout.current);
    startNavigationTimeout.current = setTimeout(() => {
      router
        .navigate(
          {
            hash: location.hash,
            search: serialize({ ...queryFromUrl, ...newQuery }) || "?",
          },
          {
            replace: (options?.navigateMode ?? navigateMode) === "replace",
            state: location.state,
          },
        )
        .then(() => {
          localOptions?.onFinish?.();
        });
    });
  };

  const state = disablePersistence ? localState : targetQuery;

  return [state, useMemoizedFn(setState)] as const;
};

// https://github.com/ljharb/qs/issues/91
const coerceValuesDecoder: Required<IParseOptions>["decoder"] = (
  str: string,
  defaultDecoder: qs.defaultDecoder,
  charset: string,
  type: "key" | "value",
) => {
  const isNumeric = (str: string) => !isNaN(Number(str)) && str.trim() !== "";
  if (type === "value" && isNumeric(str)) {
    return parseFloat(str);
  }

  const keywordMap = {
    true: true,
    false: false,
    null: null,
    undefined: undefined,
  } as const;

  if (type === "value" && str in keywordMap) {
    return keywordMap[str as keyof typeof keywordMap];
  }

  return defaultDecoder(str, defaultDecoder, charset);
};

// const encodeSpaces = (str: string) => {
//   return str.replace(/\+/g, "%2B");
// };

// const preservePlusDecoder = (
//   str: string,
//
//   type: "key" | "value",
// ) => {
//   if (type === "value") {
//     str = encodeSpaces(str);
//   }
//
//   return str;
// };

export { useUrlState };
